/**
 * Entity types that can be carried in a QR code or a links URL, and how to find each one.
 *
 * `Key` is the entity's default lookup key: `qr` (a minted, printable code column) or `guid`.
 * `Alias` is the compact path segment; aliases and names are matched case-insensitively and must
 * never collide with the reserved first segments (`qr`, `action`, `document`, `g`, `guid`, `v2`…).
 */

/**
 * @typedef {Object} EntityDefinition
 * @property {string} Entity - Meadow entity name, PascalCase.
 * @property {string} Alias - Compact path segment.
 * @property {'qr'|'guid'} Key - Default lookup key.
 * @property {string} [Column] - Column holding the QR code (for `qr`).
 * @property {string} GUIDColumn - Column holding the GUID.
 * @property {string} IDColumn - Primary key column.
 * @property {boolean} [Mint] - True when new codes are generated for this entity.
 * @property {string} [NameColumn] - Column shown as the record's display name.
 * @property {string} [Label] - Plain-language singular noun for user-facing text.
 */

/** @type {Record<string, EntityDefinition>} */
const DEFAULT_ENTITIES =
{
	Sample: { Entity: 'Sample', Alias: 'S', Key: 'qr', Column: 'QRCode', GUIDColumn: 'GUIDSample', IDColumn: 'IDSample', Mint: true, NameColumn: 'Name', Label: 'sample' },
	Location: { Entity: 'Location', Alias: 'L', Key: 'qr', Column: 'QRCode', GUIDColumn: 'GUIDLocation', IDColumn: 'IDLocation', Mint: false, NameColumn: 'Name', Label: 'location' },
	Core: { Entity: 'Core', Alias: 'C', Key: 'qr', Column: 'QRCode', GUIDColumn: 'GUIDCore', IDColumn: 'IDCore', Mint: false, NameColumn: 'Name', Label: 'core' },
	Document: { Entity: 'Document', Alias: 'D', Key: 'guid', GUIDColumn: 'GUIDDocument', IDColumn: 'IDDocument', NameColumn: 'Name', Label: 'document' },
	Project: { Entity: 'Project', Alias: 'P', Key: 'guid', GUIDColumn: 'GUIDProject', IDColumn: 'IDProject', NameColumn: 'Name', Label: 'project' },
	Observation: { Entity: 'Observation', Alias: 'O', Key: 'guid', GUIDColumn: 'GUIDObservation', IDColumn: 'IDObservation', NameColumn: 'Name', Label: 'observation' },
};

/** First path segments that can never be an entity name or alias. */
const RESERVED_SEGMENTS = [ 'qr', 'action', 'document', 'g', 'guid', 'id', 'auth', 'v2', 'v3', 'v4' ];

class EntityRegistry
{
	/**
	 * @param {Record<string, Partial<EntityDefinition>>} [pOverrides] - Entities to add or override, by name.
	 */
	constructor(pOverrides)
	{
		/** @type {Record<string, EntityDefinition>} */
		this.entities = {};
		for (const tmpDefinition of Object.values(DEFAULT_ENTITIES))
		{
			this.addEntity(tmpDefinition);
		}
		if (pOverrides && typeof pOverrides === 'object')
		{
			for (const [ tmpName, tmpDefinition ] of Object.entries(pOverrides))
			{
				this.addEntity(Object.assign({}, this.entities[tmpName] || {}, tmpDefinition, { Entity: tmpName }));
			}
		}
	}

	/**
	 * @param {EntityDefinition} pDefinition
	 */
	addEntity(pDefinition)
	{
		if (!pDefinition || !pDefinition.Entity || !pDefinition.Alias)
		{
			throw new Error('An entity definition needs at least Entity and Alias.');
		}
		const tmpName = pDefinition.Entity.toLowerCase();
		const tmpAlias = pDefinition.Alias.toLowerCase();
		if (RESERVED_SEGMENTS.includes(tmpAlias))
		{
			throw new Error(`Entity alias "${pDefinition.Alias}" is a reserved path segment.`);
		}
		for (const tmpExisting of Object.values(this.entities))
		{
			if (tmpExisting.Entity === pDefinition.Entity)
			{
				continue;
			}
			if (tmpExisting.Alias.toLowerCase() === tmpAlias || tmpExisting.Entity.toLowerCase() === tmpAlias || tmpExisting.Alias.toLowerCase() === tmpName)
			{
				throw new Error(`Entity "${pDefinition.Entity}" collides with "${tmpExisting.Entity}".`);
			}
		}
		this.entities[pDefinition.Entity] = Object.assign({ Key: 'guid', Label: pDefinition.Entity.toLowerCase() }, pDefinition);
	}

	/**
	 * Look up an entity by name or alias, case-insensitively.
	 *
	 * @param {string} pNameOrAlias
	 * @return {EntityDefinition|null}
	 */
	getEntity(pNameOrAlias)
	{
		if (typeof pNameOrAlias !== 'string' || !pNameOrAlias)
		{
			return null;
		}
		const tmpNeedle = pNameOrAlias.toLowerCase();
		for (const tmpDefinition of Object.values(this.entities))
		{
			if (tmpDefinition.Entity.toLowerCase() === tmpNeedle || tmpDefinition.Alias.toLowerCase() === tmpNeedle)
			{
				return tmpDefinition;
			}
		}
		return null;
	}

	/**
	 * @return {Array<EntityDefinition>}
	 */
	listEntities()
	{
		return Object.values(this.entities);
	}
}

module.exports = EntityRegistry;
module.exports.DEFAULT_ENTITIES = DEFAULT_ENTITIES;
module.exports.RESERVED_SEGMENTS = RESERVED_SEGMENTS;
